import type { ReferenceTranscript, ReferenceTranscriptWord } from './transcript';

import { ApiService, TaskResultMessageTypeEnum, UploadUrlsRequestTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';

const TRANSCRIBE_TASK_NAME = 'transcribe_url_direct';

function logFailure(stage: string, error: unknown): null {
    // eslint-disable-next-line no-console
    console.warn(`[codeo] extractTranscript failed: ${stage}`, error);
    return null;
}

function isTranscriptWord(value: unknown): value is ReferenceTranscriptWord {
    if (!value || typeof value !== 'object') return false;
    const word = value as Record<string, unknown>;
    return typeof word.w === 'string' && typeof word.s === 'number' && typeof word.e === 'number';
}

function parseTranscriptPayload(payload: unknown): ReferenceTranscript | null {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload as Record<string, unknown>;
    const { words, language_code, has_voiceover } = data;
    if (
        !Array.isArray(words) ||
        !words.every(isTranscriptWord) ||
        typeof language_code !== 'string' ||
        typeof has_voiceover !== 'boolean'
    ) {
        return null;
    }
    return {
        words,
        languageCode: language_code,
        hasVoiceover: has_voiceover,
    };
}

export async function extractTranscript(
    file: File,
    languageCode?: string,
): Promise<ReferenceTranscript | null> {
    let uploadUrl: string;
    let publicUrl: string;
    try {
        const { data } = await ApiService.uploadUrlsCreate({
            body: {
                temporary: true,
                file_name: file.name,
                type: UploadUrlsRequestTypeEnum.SINGLE_REQUEST_UPLOAD,
            },
        });
        uploadUrl = data.upload_url;
        // The temporary bucket uses path-style signed URLs (storage.googleapis.com/
        // temporarythirty/...), which are already the canonical public URL once the
        // query string (auth signature) is stripped. (This bucket isn't the
        // same-origin upload host, so the URL isn't rewritten.)
        publicUrl = data.upload_url.split('?')[0];
    } catch (error) {
        return logFailure('uploadUrlsCreate', error);
    }

    try {
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
            },
        });
        if (!response.ok) {
            return logFailure('gcs PUT', new Error(`status ${response.status}`));
        }
    } catch (error) {
        return logFailure('gcs PUT', error);
    }

    let taskId: string;
    try {
        const { data } = await ApiService.transcribeUrlCreate({
            body: {
                gcs_url: publicUrl,
                language_code: languageCode,
            },
        });
        taskId = data.task_id;
    } catch (error) {
        return logFailure('transcribeUrlCreate', error);
    }

    try {
        const result = await WebSocketClient.getInstance().waitForMessage<unknown>(
            taskId,
            TaskResultMessageTypeEnum.TASK_RESULT,
            TRANSCRIBE_TASK_NAME,
        );
        const transcript = parseTranscriptPayload(result);
        if (!transcript) {
            return logFailure('parse transcript payload', result);
        }
        return transcript;
    } catch (error) {
        return logFailure('waitForMessage', error);
    }
}
